import {Alert, Button, Group, Paper, Stack, Text, Textarea, TextInput, Title} from '@mantine/core'
import type {ReactNode} from 'react'
import {useEffect, useState} from 'react'

import {useEditRights} from '../jobs/edit-rights-context'
import type {ProjectDetail} from './schemas'
import {usePatchProject} from './use-projects'

type ProjectDetailsSectionProps = {
    project: ProjectDetail
}

const rowBorder = '1px solid var(--mantine-color-gray-2)'

function DetailRow({
    label,
    children,
    withBorder = true,
}: {
    label: string
    children: ReactNode
    withBorder?: boolean
}) {
    return (
        <Group
            gap="md"
            wrap="nowrap"
            align="flex-start"
            py="xs"
            style={{borderBottom: withBorder ? rowBorder : undefined}}
        >
            <Text size="xs" c="dimmed" w={90} style={{flexShrink: 0}}>
                {label}
            </Text>
            {children}
        </Group>
    )
}

export function ProjectDetailsSection({project}: ProjectDetailsSectionProps) {
    const {canEdit} = useEditRights()
    const patchProject = usePatchProject(project.id)
    const [isEditing, setIsEditing] = useState(false)
    const [description, setDescription] = useState(project.description)
    const [notes, setNotes] = useState(project.notes ?? '')
    const [poNumber, setPoNumber] = useState(project.poNumber ?? '')
    const [saveError, setSaveError] = useState<string | null>(null)

    useEffect(() => {
        setDescription(project.description)
        setNotes(project.notes ?? '')
        setPoNumber(project.poNumber ?? '')
    }, [project.id, project.description, project.notes, project.poNumber])

    async function handleSave() {
        const trimmedDescription = description.trim()
        if (!trimmedDescription) {
            setSaveError('Description is required')
            return
        }

        setSaveError(null)
        try {
            await patchProject.mutateAsync({
                name: project.name,
                description: trimmedDescription,
                notes: notes.trim() || null,
                customerId: project.customerId,
                internalContactId: project.internalContact?.id ?? null,
                customerContactId: project.customerContact?.id ?? null,
                poNumber: poNumber.trim() || null,
            })
            setIsEditing(false)
        } catch (saveFailure) {
            setSaveError(
                saveFailure instanceof Error
                    ? saveFailure.message
                    : 'Failed to update order details',
            )
        }
    }

    function handleCancel() {
        setDescription(project.description)
        setNotes(project.notes ?? '')
        setPoNumber(project.poNumber ?? '')
        setSaveError(null)
        setIsEditing(false)
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap={4}>
                <Group justify="space-between" align="center" mb={6}>
                    <Title order={4}>Order details</Title>
                    {!isEditing && canEdit ? (
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setIsEditing(true)}
                        >
                            Edit
                        </Button>
                    ) : null}
                </Group>

                <DetailRow label="Customer" withBorder={!isEditing}>
                    <Text size="sm" fw={500}>
                        {project.customerName}
                    </Text>
                </DetailRow>

                {isEditing ? (
                    <Stack gap="sm" pt="xs">
                        <Textarea
                            label="Description"
                            value={description}
                            onChange={(event) => setDescription(event.currentTarget.value)}
                            autosize
                            minRows={2}
                            error={saveError && !description.trim() ? saveError : null}
                        />
                        <TextInput
                            label="PO number (optional)"
                            value={poNumber}
                            onChange={(event) => setPoNumber(event.currentTarget.value)}
                        />
                        <Textarea
                            label="Notes"
                            description="Shared with drivers assigned to jobs on this order."
                            value={notes}
                            onChange={(event) => setNotes(event.currentTarget.value)}
                            autosize
                            minRows={2}
                            placeholder="Optional internal notes"
                        />
                        {saveError && description.trim() ? (
                            <Alert color="red" title="Could not update order details">
                                {saveError}
                            </Alert>
                        ) : null}
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                size="compact-sm"
                                onClick={handleCancel}
                                disabled={patchProject.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="compact-sm"
                                onClick={() => void handleSave()}
                                loading={patchProject.isPending}
                            >
                                Save
                            </Button>
                        </Group>
                    </Stack>
                ) : (
                    <>
                        <DetailRow label="Description" withBorder={Boolean(project.notes)}>
                            <Text size="sm" style={{whiteSpace: 'pre-wrap'}}>
                                {project.description}
                            </Text>
                        </DetailRow>
                        <DetailRow label="PO number" withBorder={Boolean(project.notes)}>
                            <Text size="sm">{project.poNumber ?? 'None'}</Text>
                        </DetailRow>
                        {project.notes ? (
                            <DetailRow label="Notes" withBorder={false}>
                                <Text size="sm" c="dimmed" style={{whiteSpace: 'pre-wrap'}}>
                                    {project.notes}
                                </Text>
                            </DetailRow>
                        ) : null}
                    </>
                )}
            </Stack>
        </Paper>
    )
}
